import "@fontsource-variable/schibsted-grotesk";
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/layout.css";
import "./styles/components.css";
import "./styles/home.css";
import "./styles/app.css";
import "./styles/admin.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router";

import { NotFound, RouteError } from "./components/ErrorState";
import { Layout } from "./components/Layout";
import { RequireAdmin, RequireAuth } from "./components/RequireAuth";
import { ToastProvider } from "./components/Toast";
import { ApiError } from "./lib/api";
import { initSentry } from "./lib/sentry";
import { Account } from "./pages/Account";
import { AdminData } from "./pages/admin/AdminData";
import { AdminLayout } from "./pages/admin/AdminLayout";
import { AdminStats } from "./pages/admin/AdminStats";
import { AdminUsers } from "./pages/admin/AdminUsers";
import { Confirm, Login, LoginCallback, Unsubscribe } from "./pages/Auth";
import { Company } from "./pages/Company";
import { Dashboard } from "./pages/Dashboard";
import { Home } from "./pages/Home";
import { Import } from "./pages/Import";
import { LegalNotice, Privacy } from "./pages/Legal";
import { RecipientConfirm, RecipientRemoval } from "./pages/Recipient";

// First, so errors thrown while the app starts are reported too.
const rootOptions = initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      // Client errors will not fix themselves; network hiccups might.
      retry: (count, error) => !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
    },
  },
});

const router = createBrowserRouter([
  {
    element: <Layout />,
    errorElement: <RouteError />,
    children: [
      { path: "/", element: <Home /> },
      { path: "/connexion", element: <Login /> },
      { path: "/connexion/:token", element: <LoginCallback /> },
      { path: "/confirmer/:token", element: <Confirm /> },
      { path: "/desinscription/:token", element: <Unsubscribe /> },
      { path: "/destinataire/:token", element: <RecipientConfirm /> },
      { path: "/destinataire/:token/retrait", element: <RecipientRemoval /> },
      { path: "/mentions-legales", element: <LegalNotice /> },
      { path: "/confidentialite", element: <Privacy /> },
      {
        path: "/tableau-de-bord",
        element: (
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        ),
      },
      {
        path: "/entreprise/:siren",
        element: (
          <RequireAuth>
            <Company />
          </RequireAuth>
        ),
      },
      {
        path: "/importer",
        element: (
          <RequireAuth>
            <Import />
          </RequireAuth>
        ),
      },
      {
        path: "/compte",
        element: (
          <RequireAuth allowPending>
            <Account />
          </RequireAuth>
        ),
      },
      {
        path: "/admin",
        element: (
          <RequireAdmin>
            <AdminLayout />
          </RequireAdmin>
        ),
        children: [
          { index: true, element: <AdminStats /> },
          { path: "utilisateurs", element: <AdminUsers /> },
          { path: "donnees", element: <AdminData /> },
          { path: "donnees/:table", element: <AdminData /> },
        ],
      },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

createRoot(document.getElementById("root")!, rootOptions).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
